import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Shield, Cloud, Server, Database, AlertTriangle, CheckCircle, Download, Upload, RefreshCw, Search, Filter, ChevronRight, ArrowLeft, Bug } from 'lucide-react';
import { getCatalogDashboard, getControls, getCatalogs, syncCatalog, exportCatalog, getMisconfigurations, getMisconfigurationsSummary, CLOUD_PROVIDERS, CONTROL_DOMAINS, SEVERITY_LEVELS, FRAMEWORKS } from '@/services/catalog';
import { toast } from 'sonner';

const MultiCloudCatalogPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [controls, setControls] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [filters, setFilters] = useState({
    provider: 'all',
    domain: 'all',
    severity: 'all',
    search: ''
  });
  const [syncing, setSyncing] = useState(false);
  const [catalogTab, setCatalogTab] = useState('controls');
  const [misconfigs, setMisconfigs] = useState([]);
  const [misconfigStats, setMisconfigStats] = useState(null);
  const [misconfigFilter, setMisconfigFilter] = useState({ provider: '', severity: '', category: '', search: '' });
  const [controlsPage, setControlsPage] = useState(1);
  const [misconfigsPage, setMisconfigsPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    loadData();
    loadControls();
  }, []);

  useEffect(() => {
    if (!loading) {
      loadControls();
    }
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dashboardData, catalogsData] = await Promise.all([
        getCatalogDashboard(),
        getCatalogs()
      ]);
      setDashboard(dashboardData);
      setCatalogs(catalogsData);
    } catch (error) {
      toast.error('Failed to load catalog data');
    } finally {
      setLoading(false);
    }
  };

  const loadControls = async () => {
    try {
      console.log('Loading controls with filters:', filters);
      const params = {};
      if (filters.provider && filters.provider !== 'all') params.provider = filters.provider;
      if (filters.domain && filters.domain !== 'all') params.domain = filters.domain;
      if (filters.severity && filters.severity !== 'all') params.severity = filters.severity;
      if (filters.search) params.search = filters.search;
      const data = await getControls(params);
      console.log('Controls loaded:', data?.length);
      setControls(data || []);
    } catch (error) {
      console.error('Failed to load controls:', error);
      toast.error('Failed to load controls');
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await syncCatalog();
      toast.success('Catalog synced to database');
      await loadData();
    } catch (error) {
      toast.error('Failed to sync catalog');
    } finally {
      setSyncing(false);
    }
  };

  const loadMisconfigs = async () => {
    try {
      const params = {};
      if (misconfigFilter.provider) params.provider = misconfigFilter.provider;
      if (misconfigFilter.severity) params.severity = misconfigFilter.severity;
      if (misconfigFilter.category) params.category = misconfigFilter.category;
      if (misconfigFilter.search) params.search = misconfigFilter.search;
      const [data, summary] = await Promise.all([
        getMisconfigurations(params),
        getMisconfigurationsSummary(),
      ]);
      setMisconfigs(data?.misconfigurations || []);
      setMisconfigStats(summary);
    } catch {
      toast.error('Failed to load misconfigurations');
    }
  };

  useEffect(() => {
    if (catalogTab === 'misconfigurations') loadMisconfigs();
  }, [catalogTab, misconfigFilter]);

  const handleExport = async (catalogId) => {
    try {
      const blob = await exportCatalog(catalogId, 'yaml');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${catalogId}.yaml`;
      a.click();
      toast.success('Catalog exported');
    } catch (error) {
      toast.error('Failed to export catalog');
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-600 text-white',
      high: 'bg-orange-500 text-white',
      medium: 'bg-yellow-500 text-black',
      low: 'bg-blue-500 text-white',
      info: 'bg-gray-400 text-white'
    };
    return colors[severity] || 'bg-gray-400';
  };

  const getProviderColor = (provider) => {
    const colors = {
      aws: 'bg-orange-500',
      azure: 'bg-blue-500',
      gcp: 'bg-green-500',
      onprem: 'bg-gray-500'
    };
    return colors[provider] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/grc')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-indigo-600" />
              Multi-Cloud Control Catalog
            </h1>
            <p className="text-gray-500">Unified security controls for AWS, Azure, GCP and On-Prem</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSync} disabled={syncing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Sync to DB
          </Button>
          <Button variant="outline" onClick={() => handleExport('beskar-multi-cloud-baseline')}>
            <Download className="w-4 h-4 mr-2" />
            Export YAML
          </Button>
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white" data-testid="catalog-total-controls-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100 text-sm">Total Controls</p>
                  <p className="text-3xl font-bold" data-testid="catalog-total-controls-value">{dashboard.total_controls}</p>
                  {dashboard.controls_by_source && (
                    <div className="mt-2 flex flex-wrap gap-1 text-[10px] text-indigo-50">
                      {Object.entries(dashboard.controls_by_source).map(([src, cnt]) => (
                        <span key={src} className="px-1.5 py-0.5 rounded bg-white/15" data-testid={`catalog-source-${src}`}>
                          {src === 'amidala_cspm' ? 'Amidala' :
                           src === 'native_yaml' ? 'Native' :
                           src === 'prowler' ? 'Prowler' :
                           src === 'litesabre_pro' ? 'LiteSabre Pro' :
                           src === 'vulnerability_db' ? 'Vulnerabilities' :
                           src.charAt(0).toUpperCase() + src.slice(1)}: {cnt}
                        </span>
                      ))}
                    </div>
                  )}
                  {dashboard.total_vulnerabilities > 0 && (
                    <div className="mt-2 text-xs text-indigo-50" data-testid="catalog-vulnerabilities-total">
                      <Bug className="inline w-3 h-3 mr-1" />
                      {dashboard.total_vulnerabilities} CVE checks from Vulnerability DB
                    </div>
                  )}
                </div>
                <Shield className="w-10 h-10 text-indigo-200" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <p className="text-gray-500 text-sm mb-2">By Provider</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(dashboard.controls_by_provider || {}).map(([provider, count]) => (
                  <Badge key={provider} className={`${getProviderColor(provider)} text-white`}>
                    {provider.toUpperCase()}: {count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <p className="text-gray-500 text-sm mb-2">By Severity</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(dashboard.controls_by_severity || {}).map(([severity, count]) => (
                  <Badge key={severity} className={getSeverityColor(severity)}>
                    {severity}: {count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <p className="text-gray-500 text-sm mb-2">Framework Coverage</p>
              <div className="grid grid-cols-2 gap-1 text-sm">
                {Object.entries(dashboard.framework_coverage || {}).slice(0, 4).map(([fw, count]) => (
                  <div key={fw} className="flex justify-between">
                    <span className="text-gray-600">{fw.toUpperCase()}</span>
                    <span className="font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search controls..."
                  className="pl-10"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
              </div>
            </div>
            <Select value={filters.provider} onValueChange={(v) => setFilters({ ...filters, provider: v })}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Providers</SelectItem>
                {CLOUD_PROVIDERS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.domain} onValueChange={(v) => setFilters({ ...filters, domain: v })}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Domain" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Domains</SelectItem>
                {CONTROL_DOMAINS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.severity} onValueChange={(v) => setFilters({ ...filters, severity: v })}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                {SEVERITY_LEVELS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Controls / Misconfigurations Tabs */}
      <Tabs value={catalogTab} onValueChange={setCatalogTab}>
        <TabsList>
          <TabsTrigger value="controls" data-testid="controls-tab">Security Controls ({dashboard ? dashboard.total_controls : controls.length})</TabsTrigger>
          <TabsTrigger value="misconfigurations" data-testid="misconfigs-tab">
            <Bug className="w-4 h-4 mr-1" /> Misconfigurations {misconfigStats ? `(${misconfigStats.total_misconfigurations})` : ''}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="controls" className="mt-4">
      {/* Controls Table with Pagination */}
      <Card>
        <CardContent>
          {(() => {
            const totalPages = Math.ceil(controls.length / ITEMS_PER_PAGE);
            const paginatedControls = controls.slice((controlsPage - 1) * ITEMS_PER_PAGE, controlsPage * ITEMS_PER_PAGE);
            return (
              <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Domain</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>SLA</TableHead>
                <TableHead>Frameworks</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedControls.map((control) => (
                <TableRow key={control.id} className="cursor-pointer hover:bg-gray-50">
                  <TableCell className="font-mono text-sm">{control.id}</TableCell>
                  <TableCell className="max-w-[300px] truncate">{control.title}</TableCell>
                  <TableCell>
                    <Badge className={`${getProviderColor(control.provider)} text-white`}>
                      {control.provider?.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {control.domain?.replace('_', ' ')}
                  </TableCell>
                  <TableCell>
                    <Badge className={getSeverityColor(control.severity)}>
                      {control.severity}
                    </Badge>
                  </TableCell>
                  <TableCell>{control.default_sla_days}d</TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {control.frameworks?.uspcf && (
                        <Badge variant="outline" className="text-xs">USPCF</Badge>
                      )}
                      {control.frameworks?.nist_800_53 && (
                        <Badge variant="outline" className="text-xs">NIST</Badge>
                      )}
                      {control.frameworks?.iso_27001 && (
                        <Badge variant="outline" className="text-xs">ISO</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t mt-4" data-testid="controls-pagination">
              <span className="text-sm text-gray-500">
                Showing {(controlsPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(controlsPage * ITEMS_PER_PAGE, controls.length)} of {controls.length} controls
              </span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={controlsPage === 1} onClick={() => setControlsPage(1)} data-testid="controls-first-page">First</Button>
                <Button variant="outline" size="sm" disabled={controlsPage === 1} onClick={() => setControlsPage(p => p - 1)} data-testid="controls-prev-page">Prev</Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const startPage = Math.max(1, Math.min(controlsPage - 2, totalPages - 4));
                  const page = startPage + i;
                  if (page > totalPages) return null;
                  return (
                    <Button key={page} variant={controlsPage === page ? "default" : "outline"} size="sm" className="w-8 h-8 p-0" onClick={() => setControlsPage(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="sm" disabled={controlsPage === totalPages} onClick={() => setControlsPage(p => p + 1)} data-testid="controls-next-page">Next</Button>
                <Button variant="outline" size="sm" disabled={controlsPage === totalPages} onClick={() => setControlsPage(totalPages)} data-testid="controls-last-page">Last</Button>
              </div>
            </div>
          )}
              </>
            );
          })()}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="misconfigurations" className="mt-4 space-y-4">
          {/* Misconfig Summary */}
          {misconfigStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {Object.entries(misconfigStats.categories || {}).map(([cat, stats]) => (
                <Card key={cat} className="hover:border-indigo-200 transition-colors">
                  <CardContent className="p-3">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">{cat.replace('_', ' ')}</p>
                    <p className="text-xl font-bold">{stats.total}</p>
                    <div className="flex gap-1 mt-1">
                      {stats.critical > 0 && <Badge className="bg-red-600 text-white text-xs">{stats.critical}C</Badge>}
                      {stats.high > 0 && <Badge className="bg-orange-500 text-white text-xs">{stats.high}H</Badge>}
                      {stats.medium > 0 && <Badge className="bg-yellow-500 text-black text-xs">{stats.medium}M</Badge>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Misconfig Filters */}
          <Card>
            <CardContent className="p-3">
              <div className="flex flex-wrap gap-3 items-center">
                <div className="flex-1 min-w-[180px]">
                  <Input placeholder="Search misconfigurations..." value={misconfigFilter.search} onChange={(e) => setMisconfigFilter({ ...misconfigFilter, search: e.target.value })} data-testid="misconfig-search" />
                </div>
                <Select value={misconfigFilter.provider} onValueChange={(v) => setMisconfigFilter({ ...misconfigFilter, provider: v === 'all' ? '' : v })}>
                  <SelectTrigger className="w-[130px]"><SelectValue placeholder="Provider" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="aws">AWS</SelectItem>
                    <SelectItem value="azure">Azure</SelectItem>
                    <SelectItem value="gcp">GCP</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={misconfigFilter.severity} onValueChange={(v) => setMisconfigFilter({ ...misconfigFilter, severity: v === 'all' ? '' : v })}>
                  <SelectTrigger className="w-[130px]"><SelectValue placeholder="Severity" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={misconfigFilter.category} onValueChange={(v) => setMisconfigFilter({ ...misconfigFilter, category: v === 'all' ? '' : v })}>
                  <SelectTrigger className="w-[160px]"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="iam">IAM</SelectItem>
                    <SelectItem value="storage">Storage</SelectItem>
                    <SelectItem value="network_security">Network</SelectItem>
                    <SelectItem value="encryption">Encryption</SelectItem>
                    <SelectItem value="logging">Logging</SelectItem>
                    <SelectItem value="compute">Compute</SelectItem>
                    <SelectItem value="database">Database</SelectItem>
                    <SelectItem value="container_security">Container</SelectItem>
                    <SelectItem value="configuration">Config</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Misconfig Table with Pagination */}
          <Card>
            <CardContent>
              {(() => {
                const totalMisconfigPages = Math.ceil(misconfigs.length / ITEMS_PER_PAGE);
                const paginatedMisconfigs = misconfigs.slice((misconfigsPage - 1) * ITEMS_PER_PAGE, misconfigsPage * ITEMS_PER_PAGE);
                return (
                  <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>CIS Benchmark</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedMisconfigs.map((m) => (
                    <TableRow key={m.id} data-testid={`misconfig-row-${m.id}`}>
                      <TableCell className="font-mono text-xs">{m.id}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{m.title}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{m.description}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getProviderColor(m.provider)} text-white`}>
                          {m.provider?.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{m.category?.replace('_', ' ')}</TableCell>
                      <TableCell>
                        <Badge className={getSeverityColor(m.severity)}>{m.severity}</Badge>
                      </TableCell>
                      <TableCell className="text-xs font-mono text-gray-500">{m.cis_benchmark || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {misconfigs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-400 py-8">No misconfigurations found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {totalMisconfigPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t mt-4" data-testid="misconfigs-pagination">
                  <span className="text-sm text-gray-500">
                    Showing {(misconfigsPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(misconfigsPage * ITEMS_PER_PAGE, misconfigs.length)} of {misconfigs.length} misconfigurations
                  </span>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" disabled={misconfigsPage === 1} onClick={() => setMisconfigsPage(1)}>First</Button>
                    <Button variant="outline" size="sm" disabled={misconfigsPage === 1} onClick={() => setMisconfigsPage(p => p - 1)}>Prev</Button>
                    {Array.from({ length: Math.min(totalMisconfigPages, 5) }, (_, i) => {
                      const startPage = Math.max(1, Math.min(misconfigsPage - 2, totalMisconfigPages - 4));
                      const page = startPage + i;
                      if (page > totalMisconfigPages) return null;
                      return (
                        <Button key={page} variant={misconfigsPage === page ? "default" : "outline"} size="sm" className="w-8 h-8 p-0" onClick={() => setMisconfigsPage(page)}>{page}</Button>
                      );
                    })}
                    <Button variant="outline" size="sm" disabled={misconfigsPage === totalMisconfigPages} onClick={() => setMisconfigsPage(p => p + 1)}>Next</Button>
                    <Button variant="outline" size="sm" disabled={misconfigsPage === totalMisconfigPages} onClick={() => setMisconfigsPage(totalMisconfigPages)}>Last</Button>
                  </div>
                </div>
              )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MultiCloudCatalogPage;
